import React, { useState, useRef } from 'react';
import { flushSync } from 'react-dom';
import './App.css';
import colors from './utils/colors';
import typography from './utils/typography';
import Navigation from './components/layout/Navigation';
import Header from './components/layout/Header';
import CreateForm from './components/forms/CreateForm';
import Workspace from './components/layout/Workspace';
import Workspaces from './components/layout/Workspaces';
import Library from './components/layout/Library';
import Home from './components/layout/Home';
import Search from './components/layout/Search';
import HooksEditor from './components/layout/HooksEditor';
import Playbar from './components/ui/Playbar';
import SidePanel from './components/ui/SidePanel';
import MobilePlayer from './components/ui/MobilePlayer';
import CustomBottomSheet from './components/ui/CustomBottomSheet';

function App() {
  const [activeNavItem, setActiveNavItem] = useState('home');
  const activeNavItemRef = useRef('home');
  const [isCreating, setIsCreating] = useState(false);
  const [newSongs, setNewSongs] = useState([]);
  const [credits, setCredits] = useState(9990);
  const [coverSongData, setCoverSongData] = useState(null);
  const [karaokeSongData, setKaraokeSongData] = useState(null);
  const [extendSongData, setExtendSongData] = useState(null);
  const [replaceSectionData, setReplaceSectionData] = useState(null);
  const [reuseLyricsData, setReuseLyricsData] = useState(null);
  const [introSongData, setIntroSongData] = useState(null);
  const [speedSongData, setSpeedSongData] = useState(null);
  const [songVersions, setSongVersions] = useState({});
  const [currentSong, setCurrentSong] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedSongTitle, setSelectedSongTitle] = useState(null);
  const [showSongDetailsPanel, setShowSongDetailsPanel] = useState(false);
  const [mobilePlayerOpen, setMobilePlayerOpen] = useState(false);
  const [mobilePlayerSong, setMobilePlayerSong] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isCreateSheetExpanded, setIsCreateSheetExpanded] = useState(false);
  const [showHooksEditor, setShowHooksEditor] = useState(false);
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const collapseNavigationRef = useRef(null);
  const [selectedMode, setSelectedMode] = useState("Chat");
  const [selectedVersion, setSelectedVersion] = useState("v4.5");
  const [workspacesPanelWidth, setWorkspacesPanelWidth] = useState(30); // percentage
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(null);
  const [selectedSongForForm, setSelectedSongForForm] = useState(null);
  const [workspaceVisible, setWorkspaceVisible] = useState(false);
  const [bannersVisible, setBannersVisible] = useState(false);
  const [workspaces, setWorkspaces] = useState([
    {
      id: 1,
      title: 'Summer nights song',
      songCount: 2,
      timeAgo: '3h ago',
      image: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=60&h=50&fit=crop',
      songs: [
        {
          title: 'Summer Nights (#1)',
          genres: 'Dreamy Pop, Synth-pop',
          image: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=67&h=67&fit=crop',
          songIndex: 0
        },
        {
          title: 'Summer Nights (#2)',
          genres: 'Dreamy Pop, Ambient',
          image: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=67&h=67&fit=crop',
          songIndex: 1
        }
      ],
      chatHistory: [
        {
          type: 'outgoing',
          content: 'Create a dreamy pop song about summer nights'
        },
        {
          type: 'incoming',
          content: 'I\'ll create a dreamy pop song about summer nights for you.'
        },
        {
          type: 'lyrics',
          lyrics: `[Verse 1]
Stars are dancing in the sky
Warm breeze whispers you and I
Summer nights that feel so right
Hold me close until daylight

[Chorus]
These summer nights with you
Everything feels brand new
Dancing under moonlight blue
Forever me and you`,
          styles: 'Dreamy Pop, Synth-pop, Ambient'
        },
        {
          type: 'players',
          songs: [
            {
              id: Date.now() - 1000,
              title: 'Summer Nights (#1)',
              genres: 'Dreamy Pop, Synth-pop',
              duration: '2:45',
              image: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=67&h=67&fit=crop',
              lyrics: `[Verse 1]
Stars are dancing in the sky
Warm breeze whispers you and I
Summer nights that feel so right
Hold me close until daylight

[Chorus]
These summer nights with you
Everything feels brand new
Dancing under moonlight blue
Forever me and you`
            },
            {
              id: Date.now() - 999,
              title: 'Summer Nights (#2)',
              genres: 'Dreamy Pop, Ambient',
              duration: '3:10',
              image: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=67&h=67&fit=crop',
              lyrics: `[Verse 1]
Stars are dancing in the sky
Warm breeze whispers you and I
Summer nights that feel so right
Hold me close until daylight

[Chorus]
These summer nights with you
Everything feels brand new
Dancing under moonlight blue
Forever me and you`
            }
          ]
        },
        {
          type: 'createMore'
        }
      ]
    },
    {
      id: 2,
      title: 'Energetic EDM remix',
      songCount: 2,
      timeAgo: '5h ago',
      image: 'https://images.unsplash.com/photo-1614854262340-ab1ca7d079c7?w=60&h=50&fit=crop',
      songs: [
        {
          title: 'Electric Energy (#1)',
          genres: 'EDM, Future Bass',
          image: 'https://images.unsplash.com/photo-1614854262340-ab1ca7d079c7?w=67&h=67&fit=crop',
          songIndex: 0
        },
        {
          title: 'Electric Energy (#2)',
          genres: 'Progressive House, EDM',
          image: 'https://images.unsplash.com/photo-1614854262340-ab1ca7d079c7?w=67&h=67&fit=crop',
          songIndex: 1
        }
      ],
      chatHistory: [
        {
          type: 'outgoing',
          content: 'Make an energetic EDM remix'
        },
        {
          type: 'incoming',
          content: 'I\'ll create an energetic EDM remix for you.'
        },
        {
          type: 'lyrics',
          lyrics: `[Drop]
Feel the bass, lose control
Let the rhythm take your soul
Hands up high, touch the sky
We're alive tonight

[Build]
Energy rising, can you feel it
Hearts are racing, we believe it`,
          styles: 'EDM, Future Bass, Progressive House'
        },
        {
          type: 'players',
          songs: [
            {
              id: Date.now() - 2000,
              title: 'Electric Energy (#1)',
              genres: 'EDM, Future Bass',
              duration: '3:20',
              image: 'https://images.unsplash.com/photo-1614854262340-ab1ca7d079c7?w=67&h=67&fit=crop',
              lyrics: `[Drop]
Feel the bass, lose control
Let the rhythm take your soul
Hands up high, touch the sky
We're alive tonight

[Build]
Energy rising, can you feel it
Hearts are racing, we believe it`
            },
            {
              id: Date.now() - 1999,
              title: 'Electric Energy (#2)',
              genres: 'Progressive House, EDM',
              duration: '3:45',
              image: 'https://images.unsplash.com/photo-1614854262340-ab1ca7d079c7?w=67&h=67&fit=crop',
              lyrics: `[Drop]
Feel the bass, lose control
Let the rhythm take your soul
Hands up high, touch the sky
We're alive tonight

[Build]
Energy rising, can you feel it
Hearts are racing, we believe it`
            }
          ]
        },
        {
          type: 'createMore'
        }
      ]
    },
    {
      id: 3,
      title: 'Chill hip-hop vibes',
      songCount: 2,
      timeAgo: '7h ago',
      image: 'https://images.unsplash.com/photo-1619983081563-430f63602796?w=60&h=50&fit=crop',
      songs: [
        {
          title: 'City Nights (#1)',
          genres: 'Chill Hip-Hop, Lo-fi',
          image: 'https://images.unsplash.com/photo-1619983081563-430f63602796?w=67&h=67&fit=crop',
          songIndex: 0
        },
        {
          title: 'City Nights (#2)',
          genres: 'Lo-fi, Trap',
          image: 'https://images.unsplash.com/photo-1619983081563-430f63602796?w=67&h=67&fit=crop',
          songIndex: 1
        }
      ],
      chatHistory: [
        {
          type: 'outgoing',
          content: 'Create a chill hip-hop track with smooth flow'
        },
        {
          type: 'incoming',
          content: 'I\'ll create a chill hip-hop track with smooth flow for you.'
        },
        {
          type: 'lyrics',
          lyrics: `[Verse 1]
Rolling through the city lights
Everything just feels so right
Got my crew, we own the night
Living life, reaching heights

[Hook]
We keep it smooth, we keep it real
That's just how we like to feel`,
          styles: 'Chill Hip-Hop, Lo-fi, Trap'
        },
        {
          type: 'players',
          songs: [
            {
              id: Date.now() - 3000,
              title: 'City Nights (#1)',
              genres: 'Chill Hip-Hop, Lo-fi',
              duration: '2:55',
              image: 'https://images.unsplash.com/photo-1619983081563-430f63602796?w=67&h=67&fit=crop',
              lyrics: `[Verse 1]
Rolling through the city lights
Everything just feels so right
Got my crew, we own the night
Living life, reaching heights

[Hook]
We keep it smooth, we keep it real
That's just how we like to feel`
            },
            {
              id: Date.now() - 2999,
              title: 'City Nights (#2)',
              genres: 'Lo-fi, Trap',
              duration: '3:05',
              image: 'https://images.unsplash.com/photo-1619983081563-430f63602796?w=67&h=67&fit=crop',
              lyrics: `[Verse 1]
Rolling through the city lights
Everything just feels so right
Got my crew, we own the night
Living life, reaching heights

[Hook]
We keep it smooth, we keep it real
That's just how we like to feel`
            }
          ]
        },
        {
          type: 'createMore'
        }
      ]
    },
    {
      id: 4,
      title: 'A funny song for my friend',
      songCount: 2,
      timeAgo: '8h ago',
      image: 'https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?w=60&h=50&fit=crop',
      songs: [
        {
          title: 'Faded Dreams (#1)',
          genres: 'Acoustic, Indie Folk',
          image: 'https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?w=67&h=67&fit=crop',
          songIndex: 0
        },
        {
          title: 'Faded Dreams (#2)',
          genres: 'Singer-Songwriter, Folk',
          image: 'https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?w=67&h=67&fit=crop',
          songIndex: 1
        }
      ],
      chatHistory: [
        {
          type: 'outgoing',
          content: 'Write a funny song for my friend'
        },
        {
          type: 'incoming',
          content: 'I\'ll create a funny song for your friend.'
        },
        {
          type: 'lyrics',
          lyrics: `[Verse 1]
Photographs and faded dreams
Nothing's quite the way it seems
Memories of yesterday
Slowly drifting far away

[Chorus]
Hold on to what we had
The good times and the bad
Every moment that we shared
Lives on because we cared`,
          styles: 'Acoustic, Indie Folk, Singer-Songwriter'
        },
        {
          type: 'players',
          songs: [
            {
              id: Date.now() - 4000,
              title: 'Faded Dreams (#1)',
              genres: 'Acoustic, Indie Folk',
              duration: '3:30',
              image: 'https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?w=67&h=67&fit=crop',
              lyrics: `[Verse 1]
Photographs and faded dreams
Nothing's quite the way it seems
Memories of yesterday
Slowly drifting far away

[Chorus]
Hold on to what we had
The good times and the bad
Every moment that we shared
Lives on because we cared`
            },
            {
              id: Date.now() - 3999,
              title: 'Faded Dreams (#2)',
              genres: 'Singer-Songwriter, Folk',
              duration: '3:15',
              image: 'https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?w=67&h=67&fit=crop',
              lyrics: `[Verse 1]
Photographs and faded dreams
Nothing's quite the way it seems
Memories of yesterday
Slowly drifting far away

[Chorus]
Hold on to what we had
The good times and the bad
Every moment that we shared
Lives on because we cared`
            }
          ]
        },
        {
          type: 'createMore'
        }
      ]
    },
    {
      id: 5,
      title: 'Beach paradise',
      songCount: 2,
      timeAgo: '9h ago',
      image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=60&h=50&fit=crop',
      songs: [
        {
          title: 'Beach Paradise (#1)',
          genres: 'Tropical House, Chillwave',
          image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=67&h=67&fit=crop',
          songIndex: 0
        },
        {
          title: 'Beach Paradise (#2)',
          genres: 'Summer Pop, Tropical House',
          image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=67&h=67&fit=crop',
          songIndex: 1
        }
      ],
      chatHistory: [
        {
          type: 'outgoing',
          content: 'Create a tropical house song about the beach'
        },
        {
          type: 'incoming',
          content: 'I\'ll create a tropical house song about the beach for you.'
        },
        {
          type: 'lyrics',
          lyrics: `[Verse 1]
Sand between my toes
Where the ocean flows
Sun is shining bright
Everything's alright

[Chorus]
Beach waves calling me
Where I want to be
Paradise is here
Crystal waters clear`,
          styles: 'Tropical House, Chillwave, Summer Pop'
        },
        {
          type: 'players',
          songs: [
            {
              id: Date.now() - 5000,
              title: 'Beach Paradise (#1)',
              genres: 'Tropical House, Chillwave',
              duration: '2:50',
              image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=67&h=67&fit=crop',
              lyrics: `[Verse 1]
Sand between my toes
Where the ocean flows
Sun is shining bright
Everything's alright

[Chorus]
Beach waves calling me
Where I want to be
Paradise is here
Crystal waters clear`
            },
            {
              id: Date.now() - 4999,
              title: 'Beach Paradise (#2)',
              genres: 'Summer Pop, Tropical House',
              duration: '3:00',
              image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=67&h=67&fit=crop',
              lyrics: `[Verse 1]
Sand between my toes
Where the ocean flows
Sun is shining bright
Everything's alright

[Chorus]
Beach waves calling me
Where I want to be
Paradise is here
Crystal waters clear`
            }
          ]
        },
        {
          type: 'createMore'
        }
      ]
    }
  ]);

  // Mobile detection
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);



  const handleNavigate = (navItem, options = {}) => {
    // Prevent navigation to 'player' on desktop - it's handled by mobile player
    if (navItem === 'player' && !isMobile) {
      return;
    }

    // Show HooksEditor modal when hooks is clicked, but don't change activeNavItem
    if (navItem === 'hooks') {
      setShowHooksEditor(true);
      return; // Don't change the active nav item
    }

    // Close hooks modal if it's open when navigating elsewhere
    if (showHooksEditor) {
      setShowHooksEditor(false);
    }

    // Don't navigate if already on that page (use ref for most current value)
    if (navItem === activeNavItemRef.current) {
      return;
    }

    // Update ref immediately
    activeNavItemRef.current = navItem;

    // Use flushSync to force synchronous rendering - prevents race conditions
    flushSync(() => {
      setActiveNavItem(navItem);
    });

    // Handle create tab with mode specification
    if (navItem === 'create' && options.mode) {
      setSelectedMode(options.mode); // Set the selected mode to match the requested mode
    }
  };

  const handleCreateSong = async (songData) => {
    console.log('App.js handleCreateSong called with:', songData);

    // Check if user has enough credits
    if (credits < 50) {
      console.log('Not enough credits');
      return;
    }

    // Deduct credits immediately
    setCredits(prev => prev - 50);
    setIsCreating(true);

    // Use the title from songData
    const finalTitle = songData.title;
    const baseTitle = finalTitle;

    // Track the workspace ID to use for adding songs
    let targetWorkspaceId = selectedWorkspaceId;

    // If in Custom mode and no workspace selected, create workspace immediately
    if (selectedMode === 'Custom' && !selectedWorkspaceId) {
      const workspaceTitle = baseTitle;
      const songImage = "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=67&h=67&fit=crop&crop=center";

      console.log('Creating new workspace immediately from Custom mode:', workspaceTitle);
      targetWorkspaceId = handleCreateWorkspace(workspaceTitle, songImage, []);
      console.log('New workspace ID:', targetWorkspaceId);
    }

    setTimeout(() => {
      // Convert genres array to string for display
      const genresString = Array.isArray(songData.genres)
        ? songData.genres.join(', ')
        : songData.genres || "Pop, Electronic";

      // Calculate version numbers first
      const currentVersion = songVersions[baseTitle] || 0;
      const nextVersion1 = currentVersion + 1;
      const nextVersion2 = currentVersion + 2;

      console.log('Creating songs with base title:', baseTitle);
      console.log('Current version:', currentVersion);
      console.log('Next versions:', nextVersion1, nextVersion2);

      const newSongEntries = [
        {
          id: Date.now(),
          title: `${baseTitle} (#${nextVersion2})`,
          genres: genresString,
          duration: "2:48",
          image: "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=67&h=67&fit=crop&crop=center",
          status: "active",
          isNew: true
        },
        {
          id: Date.now() + 1,
          title: `${baseTitle} (#${nextVersion1})`,
          genres: genresString,
          duration: "3:15",
          image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=67&h=67&fit=crop&crop=center",
          status: "active",
          isNew: true
        }
      ];

      console.log('New song entries:', newSongEntries);

      // Update songs and versions separately
      setNewSongs(prev => [...newSongEntries, ...prev]);
      setSongVersions(prev => ({
        ...prev,
        [baseTitle]: nextVersion2
      }));

      // If in Custom mode, add songs to workspace
      if (selectedMode === 'Custom' && targetWorkspaceId) {
        // Workspace exists - append songs to existing workspace
        console.log('Adding songs to workspace:', targetWorkspaceId);
        setWorkspaces(prev => prev.map(workspace => {
          if (workspace.id === targetWorkspaceId) {
            const currentSongCount = workspace.songs.length;
            // Prepare songs with correct songIndex based on existing songs
            const workspaceSongs = newSongEntries.map((song, index) => ({
              title: song.title,
              genres: song.genres,
              image: song.image,
              songIndex: currentSongCount + index
            }));

            console.log('Adding these songs to workspace:', workspaceSongs);

            return {
              ...workspace,
              songs: [...workspaceSongs, ...workspace.songs], // Add new songs at the beginning
              songCount: workspace.songs.length + workspaceSongs.length
            };
          }
          return workspace;
        }));
      }

      setIsCreating(false);
    }, 2000);
  };

  const handleCoverSong = (songData) => {
    // Set the cover song data to populate the CreateForm
    setCoverSongData(songData);
    // Switch to create tab
    setActiveNavItem('create');
  };

  const handleKaraokeSong = (songData) => {
    // Set the karaoke song data to populate the CreateForm
    setKaraokeSongData(songData);
    // Switch to create tab
    setActiveNavItem('create');
  };

  const handleExtendSong = (songData) => {
    // Set the extend song data to populate the CreateForm in extend mode
    setExtendSongData(songData);
    // Switch to create tab
    setActiveNavItem('create');
  };

  const handleReplaceSection = (songData) => {
    // Set the replace section song data to populate the CreateForm in replace section mode
    setReplaceSectionData(songData);
    // Switch to create tab
    setActiveNavItem('create');
  };

  const handleReuseLyrics = (songData) => {
    // Set the reuse lyrics data to populate the CreateForm with lyrics and genres only
    setReuseLyricsData(songData);
    // Switch to create tab
    setActiveNavItem('create');
  };

  const handleIntroSong = (songData) => {
    // Set the intro song data to populate the CreateForm in intro mode
    setIntroSongData(songData);
    // Switch to create tab
    setActiveNavItem('create');
  };

  const handleSpeedSong = (songData) => {
    // Set the speed song data to populate the CreateForm in speed mode
    setSpeedSongData(songData);
    // Switch to create tab
    setActiveNavItem('create');
  };

  const handlePlaySong = (song, shouldCollapseNav = true) => {
    setCurrentSong(song);
    setIsPlaying(true);
    setShowSongDetailsPanel(true);
    // Collapse navigation when playing a song, unless explicitly told not to
    if (shouldCollapseNav) {
      collapseNavigationRef.current?.();
    }
  };

  // Separate handler for create section that doesn't collapse navigation
  const handlePlaySongInCreate = (song) => {
    handlePlaySong(song, false);
  };

  const handleSelectSong = (song) => {
    // Toggle selection - if the same song is selected, deselect it
    if (selectedSongTitle === song?.title) {
      setSelectedSongTitle(null);
      setSelectedSongForForm(null);
    } else {
      setSelectedSongTitle(song?.title || null);
      setSelectedSongForForm(song);
    }
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handlePauseSong = () => {
    setIsPlaying(false);
  };

  const handleNext = () => {
    console.log('Next song');
    // Implement next song logic here
  };

  const handlePrev = () => {
    console.log('Previous song');
    // Implement previous song logic here
  };

  const handleRepeat = () => {
    console.log('Repeat toggle');
    // Implement repeat logic here
  };

  const handleForward10 = () => {
    console.log('Forward 10 seconds');
    // Implement forward 10s logic here
  };

  const handleClosePlaybar = () => {
    setCurrentSong(null);
    setIsPlaying(false);
    setShowSongDetailsPanel(false);
  };

  const handleCloseSongDetailsPanel = () => {
    setShowSongDetailsPanel(false);
  };

  const handleOpenMobilePlayer = (song) => {
    setMobilePlayerSong(song);
    setMobilePlayerOpen(true);
  };

  const handleCloseMobilePlayer = () => {
    setMobilePlayerOpen(false);
    setMobilePlayerSong(null);
  };

  const handleSidePanelStateChange = (isOpen, context) => {
    // Side panel state tracking - can be used for future features
  };

  const handleCloseHooksEditor = () => {
    setShowHooksEditor(false);
  };

  const handleNavCollapseChange = (collapsed) => {
    setIsNavCollapsed(collapsed);
  };

  const handleNavigateToCreateFromHome = (inputData) => {
    // Set the mode to Chat
    setSelectedMode('Chat');
    // Navigate to create tab
    setActiveNavItem('create');
    // Store the message to send once chat is ready
    setPendingChatMessage(inputData.description);
  };

  const handleModeChange = (mode) => {
    setSelectedMode(mode);
    // Always show workspace in Custom mode
    if (mode === "Custom") {
      setWorkspaceVisible(true);
    }
  };

  const handleWorkspaceToggle = () => {
    setWorkspaceVisible(!workspaceVisible);
  };

  const handleVersionChange = (version) => {
    setSelectedVersion(version);
  };

  const handleWorkspacesPanelResize = (newWidth) => {
    setWorkspacesPanelWidth(newWidth);
  };

  const handleWorkspaceClick = (workspaceId) => {
    console.log('Workspace clicked:', workspaceId);
    setSelectedWorkspaceId(workspaceId);
  };

  const handleCreateWorkspace = (workspaceTitle, songImage, songs = [], autoSelect = true) => {
    const newWorkspace = {
      id: Date.now(),
      title: workspaceTitle,
      songCount: songs.length,
      timeAgo: 'just now',
      image: songImage || 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=60&h=50&fit=crop',
      songs: songs
    };

    setWorkspaces(prev => [newWorkspace, ...prev]);
    console.log('Created new workspace:', newWorkspace);

    // Automatically open the workspace in the sidebar only if autoSelect is true
    if (autoSelect) {
      setSelectedWorkspaceId(newWorkspace.id);
    }

    // Return the workspace ID so Chat can navigate to it
    return newWorkspace.id;
  };

  const handleOpenWorkspace = (workspaceId) => {
    setSelectedWorkspaceId(workspaceId);
  };

  const handleToggleBanners = () => {
    setBannersVisible(!bannersVisible);
  };

  const [onSendChatMessage, setOnSendChatMessage] = useState(null);
  const [pendingChatMessage, setPendingChatMessage] = useState(null);

  const handleOpenLyricsEditor = (newLyrics) => {
    console.log('Creating new lyrics version:', newLyrics);
    // Send message to chat if callback is available
    if (onSendChatMessage) {
      const message = `Make a new version of the song with these lyrics:\n\n${newLyrics}`;
      onSendChatMessage(message);
    }
  };

  const handleOpenStylesEditor = (newStyles) => {
    console.log('Creating new styles version:', newStyles);
    // Send message to chat if callback is available
    if (onSendChatMessage) {
      const message = `Make a new version of the song with these styles:\n\n${newStyles}`;
      onSendChatMessage(message);
    }
  };

  // Auto-send pending chat message when chat becomes available
  React.useEffect(() => {
    if (onSendChatMessage && pendingChatMessage && activeNavItem === 'create' && selectedMode === 'Chat') {
      // Small delay to ensure chat is fully rendered
      const timer = setTimeout(() => {
        onSendChatMessage(pendingChatMessage);
        setPendingChatMessage(null); // Clear the pending message
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [onSendChatMessage, pendingChatMessage, activeNavItem, selectedMode]);

  // Mobile create page layout with Silk bottom sheet
  if (isMobile && activeNavItem === 'create') {
    return (
      <div className="h-screen text-white overflow-hidden relative" style={{ backgroundColor: colors.bg.primary }}>
        {/* Workspace Layer - Scales and stacks behind when create sheet is expanded */}
        <div 
          className={`absolute inset-0 transition-all duration-300 ease-out ${
            isCreateSheetExpanded 
              ? 'transform scale-90 translate-y-[-10px] rounded-t-[20px] overflow-hidden' 
              : 'transform scale-100 translate-y-0'
          }`}
          style={{
            backgroundColor: isCreateSheetExpanded ? colors.bg.secondary : 'transparent'
          }}
        >
          <Workspace 
            isCreating={isCreating} 
            newSongs={newSongs}
            onCoverSong={handleCoverSong}
            onExtendSong={handleExtendSong}
            onReplaceSection={handleReplaceSection}
            onReuseLyrics={handleReuseLyrics}
            onIntroSong={handleIntroSong}
            onSpeedSong={handleSpeedSong}
            onPlaySong={handlePlaySong}
            onPauseSong={handlePauseSong}
            currentlyPlayingSong={isPlaying ? currentSong : null}
            hasPlaybar={false} // Hide playbar on mobile
            onSidePanelStateChange={handleSidePanelStateChange}
            onCollapseNavigation={() => collapseNavigationRef.current?.()}
            isMobile={true}
            hideHeader={false} // Always show header since bottom sheet handles visibility
          />
        </div>

        {/* Custom Full-Width Bottom Sheet for Create */}
        <CustomBottomSheet
          onCreateSong={handleCreateSong}
          credits={credits}
          coverSongData={coverSongData}
          onCoverSongDataUsed={() => setCoverSongData(null)}
          extendSongData={extendSongData}
          onExtendSongDataUsed={() => setExtendSongData(null)}
          replaceSectionData={replaceSectionData}
          onReplaceSectionDataUsed={() => setReplaceSectionData(null)}
          reuseLyricsData={reuseLyricsData}
          onReuseLyricsDataUsed={() => setReuseLyricsData(null)}
          introSongData={introSongData}
          onIntroSongDataUsed={() => setIntroSongData(null)}
          speedSongData={speedSongData}
          onSpeedSongDataUsed={() => setSpeedSongData(null)}
          onExpandedChange={setIsCreateSheetExpanded}
        />

        {/* Mobile Player - Full screen overlay */}
        {mobilePlayerOpen && mobilePlayerSong && (
          <MobilePlayer
            song={mobilePlayerSong}
            isPlaying={isPlaying}
            onClose={handleCloseMobilePlayer}
            onPlayPause={() => setIsPlaying(!isPlaying)}
            onNext={handleNext}
            onPrevious={handlePrev}
            onRepeat={handleRepeat}
            onForward10={handleForward10}
          />
        )}

        {/* Hooks Editor Modal */}
        {showHooksEditor && (
          <HooksEditor onClose={handleCloseHooksEditor} isNavCollapsed={isNavCollapsed} />
        )}
      </div>
    );
  }

  // Determine if we should show the header and if it should be in remix mode
  const shouldShowHeader = activeNavItem === 'create';
  const isRemixMode = !!(coverSongData || karaokeSongData || extendSongData || replaceSectionData || reuseLyricsData || introSongData || speedSongData);

  // Desktop layout (unchanged)
  return (
    <div className="h-screen text-white overflow-hidden flex" style={{ backgroundColor: colors.bg.primary }}>
      {/* Hide navigation on mobile */}
      {!isMobile && (
        <Navigation
          activeItem={showHooksEditor ? 'hooks' : activeNavItem}
          onNavigate={handleNavigate}
          credits={credits}
          onCollapseRequest={(fn) => { collapseNavigationRef.current = fn; }}
          onOpenMobilePlayer={handleOpenMobilePlayer}
          onCollapseChange={handleNavCollapseChange}
          onToggleBanners={handleToggleBanners}
        />
      )}

        <div className="flex-1 flex overflow-hidden" key={`main-${activeNavItem}`}>
          <div className="flex-1 flex flex-col overflow-hidden">
            {shouldShowHeader && (
              <Header
                selectedMode={selectedMode}
                onModeChange={handleModeChange}
                version={selectedVersion}
                onVersionChange={handleVersionChange}
                count={credits}
                isRemixMode={isRemixMode}
                workspaceVisible={workspaceVisible}
                onWorkspaceToggle={handleWorkspaceToggle}
              />
            )}

            <div className="flex-1 flex overflow-hidden relative">
          <div key="home" className="flex-1 flex" style={{ display: activeNavItem === 'home' ? 'flex' : 'none' }}>
            <Home
              hasPlaybar={!!currentSong}
              onPlaySong={handlePlaySong}
              onPauseSong={handlePauseSong}
              currentlyPlayingSong={isPlaying ? currentSong : null}
              onCreateSong={handleCreateSong}
              onNavigate={handleNavigate}
              onNavigateToCreate={handleNavigateToCreateFromHome}
              onCoverSong={handleCoverSong}
              onKaraokeSong={handleKaraokeSong}
              onExtendSong={handleExtendSong}
              onReplaceSection={handleReplaceSection}
              onReuseLyrics={handleReuseLyrics}
              onIntroSong={handleIntroSong}
              onSpeedSong={handleSpeedSong}
              onSidePanelStateChange={handleSidePanelStateChange}
              onCollapseNavigation={() => collapseNavigationRef.current?.()}
              isNavCollapsed={isNavCollapsed}
              bannersVisible={bannersVisible}
            />
          </div>
          <div key="create" style={{ display: activeNavItem === 'create' ? 'flex' : 'none', flex: 1 }}>
              {/* Panel Left - Chat/Custom Form */}
              <div className={`transition-all duration-[250ms] ease-out ${selectedMode === "Custom" ? "md:w-96 xl:w-[450px] flex-shrink-0" : "flex-1"}`}>
                <CreateForm
                  onCreateSong={handleCreateSong}
                  credits={credits}
                  isCreating={isCreating}
                  coverSongData={coverSongData}
                  onCoverSongDataUsed={() => setCoverSongData(null)}
                  karaokeSongData={karaokeSongData}
                  onKaraokeSongDataUsed={() => setKaraokeSongData(null)}
                  extendSongData={extendSongData}
                  onExtendSongDataUsed={() => setExtendSongData(null)}
                  replaceSectionData={replaceSectionData}
                  onReplaceSectionDataUsed={() => setReplaceSectionData(null)}
                  reuseLyricsData={reuseLyricsData}
                  onReuseLyricsDataUsed={() => setReuseLyricsData(null)}
                  introSongData={introSongData}
                  onIntroSongDataUsed={() => setIntroSongData(null)}
                  speedSongData={speedSongData}
                  onSpeedSongDataUsed={() => setSpeedSongData(null)}
                  hasPlaybar={!!currentSong}
                  selectedMode={selectedMode}
                  onModeChange={handleModeChange}
                  selectedVersion={selectedVersion}
                  onVersionChange={handleVersionChange}
                  onPlaySong={handlePlaySongInCreate}
                  onSelectSong={handleSelectSong}
                  selectedSongTitle={selectedSongTitle}
                  selectedSongForForm={selectedSongForForm}
                  onCreateWorkspace={handleCreateWorkspace}
                  onOpenWorkspace={handleOpenWorkspace}
                  selectedWorkspaceId={selectedWorkspaceId}
                  workspaces={workspaces}
                  workspaceChatHistory={selectedWorkspaceId ? workspaces.find(w => w.id === selectedWorkspaceId)?.chatHistory : null}
                  onOpenLyricsEditor={handleOpenLyricsEditor}
                  onOpenStylesEditor={handleOpenStylesEditor}
                  onRegisterSendMessage={setOnSendChatMessage}
                />
              </div>

              {/* Panel Right - Workspaces */}
              <div
                className={`hidden md:flex transition-all duration-[250ms] ease-out overflow-hidden border-l border-white/10 ${selectedMode === "Custom" ? "md:flex-1" : ""}`}
                style={{
                  width: selectedMode === "Custom"
                    ? undefined
                    : (workspaceVisible ? `${workspacesPanelWidth}%` : '0px'),
                  minWidth: selectedMode === "Custom"
                    ? undefined
                    : (workspaceVisible ? '500px' : '0px'),
                  maxWidth: selectedMode === "Custom"
                    ? undefined
                    : (workspaceVisible ? '50%' : '0px'),
                  flexShrink: 0,
                  opacity: (workspaceVisible || selectedMode === "Custom") ? 1 : 0
                }}
              >
                <Workspaces
                  hasPlaybar={!!currentSong}
                  onResize={handleWorkspacesPanelResize}
                  currentWidth={workspacesPanelWidth}
                  onWorkspaceClick={handleWorkspaceClick}
                  workspaces={workspaces}
                  selectedWorkspaceId={selectedWorkspaceId}
                  onPlaySong={handlePlaySongInCreate}
                  onSelectSong={handleSelectSong}
                  selectedSongTitle={selectedSongTitle}
                  currentSong={currentSong}
                  isPlaying={isPlaying}
                  selectedMode={selectedMode}
                  onModeChange={handleModeChange}
                  isCreating={isCreating}
                  onCreateWorkspace={handleCreateWorkspace}
                />
              </div>
          </div>
          <div key="workspace" className="flex-1 flex" style={{ display: activeNavItem === 'workspace' ? 'flex' : 'none' }}>
            <Workspace 
            isCreating={isCreating} 
            newSongs={newSongs}
            onCoverSong={handleCoverSong}
            onKaraokeSong={handleKaraokeSong}
            onExtendSong={handleExtendSong}
            onReplaceSection={handleReplaceSection}
            onReuseLyrics={handleReuseLyrics}
            onIntroSong={handleIntroSong}
            onSpeedSong={handleSpeedSong}
            onPlaySong={handlePlaySong}
            onPauseSong={handlePauseSong}
            currentlyPlayingSong={isPlaying ? currentSong : null}
            hasPlaybar={!!currentSong}
            onSidePanelStateChange={handleSidePanelStateChange}
            onCollapseNavigation={() => collapseNavigationRef.current?.()}
          />
          </div>
          <div key="library" className="flex-1 flex" style={{ display: activeNavItem === 'library' ? 'flex' : 'none' }}>
            <Library
              hasPlaybar={!!currentSong}
            onPlaySong={handlePlaySong}
            onPauseSong={handlePauseSong}
            currentlyPlayingSong={isPlaying ? currentSong : null}
            onSidePanelStateChange={handleSidePanelStateChange}
          />
          </div>
          <div key="search" className="flex-1 flex" style={{ display: activeNavItem === 'search' ? 'flex' : 'none' }}>
            <Search
              hasPlaybar={!!currentSong}
            />
          </div>
          <div key="placeholders" className="flex-1 flex" style={{ display: (activeNavItem === 'studio' || activeNavItem === 'explore' || activeNavItem === 'notifications') ? 'flex' : 'none' }}>
          <div
            className="flex-1 flex items-center justify-center"
            style={{
              backgroundColor: colors.bg.primary,
              width: '100%',
              height: '100%'
            }}
          >
            <div className="text-center p-8">
              <p style={{ ...typography.h3, color: colors.text.secondary, marginBottom: '8px' }}>
                {activeNavItem === 'studio' && 'Studio'}
                {activeNavItem === 'explore' && 'Explore'}
                {activeNavItem === 'notifications' && 'Notifications'}
              </p>
              <p style={{ ...typography.body, color: colors.text.tertiary }}>
                Coming soon
              </p>
            </div>
          </div>
          </div>
            </div>

          {/* Song Details Side Panel - Pushes content to the left */}
          <div
            className="transition-all duration-[250ms] ease-out flex-shrink-0"
            style={{
              width: showSongDetailsPanel ? 'auto' : '0px',
              opacity: showSongDetailsPanel ? 1 : 0,
              overflow: showSongDetailsPanel ? 'visible' : 'hidden'
            }}
          >
            {showSongDetailsPanel && currentSong && (
              <div className="h-full" style={{ position: 'relative' }}>
                <SidePanel
                  isOpen={showSongDetailsPanel}
                  onClose={handleCloseSongDetailsPanel}
                  song={currentSong}
                  hasPlaybar={!!currentSong}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Playbar - Fixed at bottom (hidden on mobile) */}
      {currentSong && !isMobile && (
        <div 
          className="fixed bottom-0 right-0 z-[70]"
          style={{
            left: isNavCollapsed ? '77px' : '221px' // Adjust based on navbar state including border width
          }}
        >
          <Playbar
            song={currentSong}
            isPlaying={isPlaying}
            onPlayPause={handlePlayPause}
            onNext={handleNext}
            onPrev={handlePrev}
            onClose={handleClosePlaybar}
          />
        </div>
      )}
      
      {/* Mobile Player - Full screen overlay */}
      {mobilePlayerOpen && mobilePlayerSong && (
        <MobilePlayer
          song={mobilePlayerSong}
          isPlaying={isPlaying}
          onClose={handleCloseMobilePlayer}
          onPlayPause={() => setIsPlaying(!isPlaying)}
          onNext={handleNext}
          onPrevious={handlePrev}
          onRepeat={handleRepeat}
          onForward10={handleForward10}
        />
      )}

      {/* Hooks Editor Modal */}
      {showHooksEditor && (
        <HooksEditor onClose={handleCloseHooksEditor} isNavCollapsed={isNavCollapsed} />
      )}
    </div>
  );
}

export default App;
